import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, Flex } from "@nubeteck/components";
import { Form, Spin, Result, Descriptions } from "antd";
import FormItem from "antd/es/form/FormItem";
import toast from "react-hot-toast";
import { toastErrorStyle } from "src/constants";
import {
  useGetPublicRequestQuery,
  useGetFieldTypesQuery,
  useSubmitPublicRequestMutation,
} from "src/services/requestsServices";
import ConfigurationInput from "../../admin-pages/generalSettings/settingsInput";

const PublicRequest: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [form] = Form.useForm();
  const [submitted, setSubmitted] = useState(false);

  const { data: requestData, isLoading } = useGetPublicRequestQuery(token);
  const { data: tiposCampos } = useGetFieldTypesQuery();
  const [submitRequest, { isLoading: isSubmitting }] = useSubmitPublicRequestMutation();

  const solicitud = requestData?.data;

  const submit = async (values: any) => {
    const camposPorId = new Map(
      solicitud?.camposAdministrativos?.map((c: any) => [c.campoId, c]) ?? [],
    );

    const detalle = Object.keys(values).map((campoId: string) => {
      const campo: any = camposPorId.get(+campoId);
      const tipoCampoNombre = tiposCampos?.data?.find(
        (t: any) => t.tipoCampoId === campo?.tipoCampoId,
      )?.tipoCampoNombre;

      let valor = values[campoId];
      if (tipoCampoNombre === "Fecha" && valor && typeof valor === "object" && valor.format) {
        valor = valor.format("YYYY/MM/DD");
      } else {
        valor = `${valor}`;
      }

      return { campoId: +campoId, valor };
    });

    try {
      await submitRequest({ token: token!, body: { detalle } }).unwrap();
      setSubmitted(true);
    } catch {
      toast.error("Ha ocurrido un error al enviar los datos.", toastErrorStyle);
    }
  };

  if (isLoading) {
    return (
      <Flex style={{ justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (solicitud?.yaCompletado) {
    return (
      <Flex style={{ justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Result
          status="info"
          title="Datos administrativos ya completados"
          subTitle="Los datos administrativos para esta solicitud ya fueron completados."
        />
      </Flex>
    );
  }

  if (submitted) {
    return (
      <Flex style={{ justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Result
          status="success"
          title="Datos enviados correctamente"
          subTitle="Los datos administrativos han sido guardados. Puede cerrar esta pestaña."
        />
      </Flex>
    );
  }

  if (!solicitud) {
    return (
      <Flex style={{ justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Result
          status="error"
          title="Solicitud no encontrada"
          subTitle="El enlace proporcionado no es válido o ha expirado."
        />
      </Flex>
    );
  }

  return (
    <Flex
      style={{
        justifyContent: "center",
        alignItems: "flex-start",
        minHeight: "100vh",
        padding: 24,
        backgroundColor: "#f5f5f5",
      }}
    >
      <Card
        style={{ maxWidth: 600, width: "100%" }}
        title={solicitud.solicitudNombre ?? "Datos administrativos"}
      >
        <Descriptions bordered size="small" column={1} style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Solicitud #">
            {solicitud.estudianteSolicitudId}
          </Descriptions.Item>
          <Descriptions.Item label="Id">{solicitud.estudianteIdentificacion}</Descriptions.Item>
          <Descriptions.Item label="Estudiante">{solicitud.nombreCompleto}</Descriptions.Item>
        </Descriptions>

        <Form
          form={form}
          layout="vertical"
          onFinish={submit}
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          {solicitud.camposAdministrativos?.map((campo: any, index: number) => (
            <div key={`${campo.tipoCampoId}-${index}`}>
              <FormItem
                rules={[
                  {
                    required: campo.esObligatorio,
                    message: campo.esObligatorio ? "Este campo es requerido" : "",
                  },
                ]}
                label={campo.campoNombre}
                name={campo.campoId}
                help={campo.informacionAdicional}
              >
                <ConfigurationInput
                  data={{
                    id: campo.campoId,
                    label: campo.campoNombre,
                    nombre: campo.campoNombre,
                    opciones: campo.opciones?.map((op: any) => ({
                      opcion: op.opcion,
                      opcionId: op.opcion,
                    })),
                    tipo: tiposCampos?.data?.find(
                      (tipo: any) => tipo.tipoCampoId === campo.tipoCampoId,
                    )?.tipoCampoNombre,
                    descripcion: campo.informacionAdicional,
                    valor: null,
                  }}
                  onChange={(value) => form.setFieldValue(campo.campoId, value)}
                />
              </FormItem>
            </div>
          ))}

          <Button
            type="primary"
            size="large"
            htmlType="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            style={{ alignSelf: "flex-end" }}
          >
            Enviar
          </Button>
        </Form>
      </Card>
    </Flex>
  );
};

export default PublicRequest;
